<template>
    <div class="row">
        <div class="col-12" :class="{ 'col-md-6': _get(goal, 'parameters.time_limit', false) }">
            <div class="d-flex flex-column justify-content-between align-items-center h-100">
                <div
                    class="tally-background d-flex justify-content-center align-center cursor-pointer"
                    @click="tallyUp()"
                    dusk="tally-up"
                >
                    <h2 class="mb-0 text-white font-weight-bold">{{ tally }}</h2>
                </div>
            </div>
        </div>
        <div
            :class="{ 'd-none': !_get(goal, 'parameters.time_limit', false) }"
            class="col-12 col-md-6 cursor-pointer"
            @click="running ? null : startTimer()"
        >
            <div
                class="d-flex flex-column justify-content-between align-items-center h-100 mt-2 mt-md-0"
            >
                <div
                    :id="`tally-timer-${goal ? goal.id : 0}`"
                    class="tally-timer prog-circle d-flex flex-column justify-content-center align-center position-relative w-100"
                >
                    <h3
                        class="position-absolute font-weight-bold mb-0 w-100 text-center"
                        :class="{
                            'text-danger':
                                goal &&
                                goal.parameters.time_limit &&
                                elapsedTime >= goal.parameters.time_limit,
                        }"
                    >
                        {{ running ? elapsedDisplay : "START" }}
                    </h3>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "Tally",
    props: ["goal", "date", "customization", "notesData"],
    data() {
        return {
            startTime: this.moment(),
            currentTime: this.moment(),
            interval: null,
            running: false,
            timerMax: this.goal ? this.goal.parameters.time_limit || 60 : 60,
            tally: 0,
            progress: null,
            fade: "simple-fade",
        };
    },

    mounted() {
        this.$forceUpdate();
        this.progress = new this.ProgressBar.Circle(
            `#tally-timer-${this.goal ? this.goal.id : 0}`,
            {
                strokeWidth: 7,
                easing: "easeInOut",
                duration: 1,
                color: "#3da2d8",
                trailColor: "#c6c6c8",
                trailWidth: 7,
                svgStyle: null,
            }
        );
    },

    computed: {
        elapsedTime() {
            if (this.goal && this.goal.parameters.time_limit) {
                return (
                    Math.min(
                        this.goal.parameters.time_limit,
                        this.currentTime.diff(this.startTime, "seconds")
                    ) || 0
                );
            }
            return this.currentTime.diff(this.startTime, "seconds") || 0;
        },

        elapsedDisplay() {
            if (this.goal && this.goal.parameters.time_limit) {
                const remaining = Math.min(this.timerMax - this.elapsedTime, this.timerMax);
                return this.moment()
                    .startOf("day")
                    .seconds(remaining || 0)
                    .format("H:mm:ss");
            } else {
                return this.moment()
                    .startOf("day")
                    .seconds(this.elapsedTime || 0)
                    .format("H:mm:ss");
            }
        },
    },

    methods: {
        _get(obj, path, def) {
            return window._.get(obj, path, def);
        },

        tallyUp() {
            if (this.tally === 0 && this.elapsedTime === 0) {
                this.startTimer();
            }
            if (
                this.goal.parameters.time_limit &&
                this.elapsedTime >= this.goal.parameters.time_limit
            ) {
            } else {
                this.tally++;
            }
        },

        startTimer() {
            this.running = true;
            this.startTime = this.moment();
            this.interval = setInterval(this.animate, 1000);
        },

        stopTimer() {
            this.running = false;
            clearInterval(this.interval);
        },

        animate() {
            this.currentTime = this.moment();
            if (this.goal && this.goal.parameters.time_limit) {
                this.progress.animate(
                    Math.max(this.timerMax - this.elapsedTime, 0) / this.timerMax
                );
            } else {
                this.progress.animate((this.elapsedTime % this.timerMax) / this.timerMax);
            }
        },

        saveTrackerData() {
            let data = {
                goal_id: this.goal.id,
                data: {
                    type: "tally",
                    success: this.tally,
                },
                notes: this.notesData,
                session_at: this.date
                    ? this.moment(this.date).format("YYYY-MM-DD HH:mm:ss")
                    : this.moment().format("YYYY-MM-DD HH:mm:ss"),
            };
            axios
                .post(`/api/patients/${this.goal.patient.id}/goals/${this.goal.id}/trackers`, data)
                .then(r => {
                    this.reportResult(true);
                })
                .catch(error => {
                    this.$emit("save-draft", { error, data });
                    this.reportResult(false);
                    this.$swal({
                        title: "Error!",
                        text: "An error occurred while saving.",
                        type: "error",
                        confirmButtonText: "Close",
                        confirmButtonColor: "#d64571",
                    });
                    console.error(error);
                });
        },

        reportResult(success) {
            this.$emit("saved", success);
            if (success) {
                this.$emit(
                    "close-tracker",
                    this.goal.title + ": " + `Scored ${this.tally} times` + " - " + this.notesData
                );
            }
        },

        undoAttempt() {
            if (this.tally > 0) this.tally--;
        },
    },
};
</script>

<style scoped lang="scss">
.add-remove {
    transition: all 0.4s;
    top: -0.5rem;
    right: 0.5rem;
    min-width: 1rem;
    max-width: 1rem;
    min-height: 1rem;
    max-height: 1rem;
    > i {
        transition: all 0.4s;
    }
}

.tally-background {
    background-image: url("../../../../images/trackers/Tally.png");
    background-size: contain;
    width: 100%;
    min-width: 50%;
    background-repeat: no-repeat;
    background-position: center;
    height: 100%;

    > h2 {
        line-height: 0.95;
        font-size: 6rem;
        font-family: "Roboto Condensed", sans-serif;
    }
}

.tally-timer {
    max-width: 30%;
    min-width: 100px;
    border-radius: 50%;
}

.prog-circle > h5 {
    font-family: "Roboto Condensed", sans-serif;
}
</style>
